
import { useState, useEffect, useCallback } from 'react';

const slides = [
  {
    id: 1,
    image: 'https://public.readdy.ai/ai/img_res/edited_cdb6536c35406fd7e2c6d39cd023f9fd_f0cabfba.jpg',
    alt: "ABEXIA Women's Fashion Collection",
    tag: "Women's Fashion",
    heading: <>Timeless Elegance,<br />Modern Grace</>,
    sub: "Curated women's fashion crafted for the bold, beautiful & confident woman",
    primaryLabel: "Shop Women's Collection",
    primaryHref: '#collections',
    secondaryLabel: 'Explore Party Bags',
    secondaryHref: '#party-bags',
    overlay: 'from-black/50 via-black/20 to-black/55',
    objectPosition: 'object-top',
  },
  {
    id: 2,
    image: 'https://static.readdy.ai/image/14e1a4067fff913af615b9e7ea97ac6c/fb7a1a61dd88c4989b0912a85b7769d4.jpeg',
    alt: 'Kids Party Bags Collection',
    tag: "Children's Party Gifts",
    heading: <>Magical Moments,<br />Unforgettable Bags</>,
    sub: "Beautifully themed party bags that make every child's celebration extra special",
    primaryLabel: 'Shop Party Bags',
    primaryHref: '#party-bags',
    secondaryLabel: "Women's Collection",
    secondaryHref: '#collections',
    overlay: 'from-black/60 via-black/30 to-black/60',
    objectPosition: 'object-center',
  },
];

export default function Hero() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  const goTo = useCallback((index: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(index);
      setAnimating(false);
    }, 400);
  }, [animating]);

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo]);

  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next]);

  const slide = slides[current];

  return (
    <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
      {/* Background image */}
      <div
        className={`absolute inset-0 transition-opacity duration-500 ${animating ? 'opacity-0' : 'opacity-100'}`}
      >
        <img
          src={slide.image}
          alt={slide.alt}
          className={`w-full h-full object-cover ${slide.objectPosition}`}
        />
        <div className={`absolute inset-0 bg-gradient-to-b ${slide.overlay}`}></div>
      </div>

      {/* Content */}
      <div
        className={`relative z-10 text-center px-6 w-full max-w-5xl mx-auto transition-all duration-500 ${animating ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}
      >
        <span className="inline-block text-sm uppercase tracking-[0.3em] text-[#EB9830] mb-6 font-medium">
          {slide.tag}
        </span>
        <h1 className="text-5xl md:text-7xl font-serif text-white mb-8 leading-tight">
          {slide.heading}
        </h1>
        <p className="text-xl md:text-2xl text-white/90 mb-12 font-light max-w-2xl mx-auto">
          {slide.sub}
        </p>
        <div className="flex items-center justify-center gap-4 flex-wrap">
          <a
            href={slide.primaryHref}
            className="inline-block px-10 py-4 bg-[#EB9830] text-white text-base font-medium rounded-full hover:bg-[#936A0B] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            {slide.primaryLabel}
          </a>
          <a
            href={slide.secondaryHref}
            className="inline-block px-10 py-4 bg-white/10 backdrop-blur-sm border border-white/40 text-white text-base font-medium rounded-full hover:bg-white/20 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            {slide.secondaryLabel}
          </a>
        </div>
      </div>

      {/* Arrow buttons */}
      <button
        onClick={prev}
        aria-label="Previous slide"
        className="absolute left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-200 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={next}
        aria-label="Next slide"
        className="absolute right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-white hover:bg-white/30 transition-all duration-200 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      {/* Dot indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((s, i) => (
          <button
            key={s.id}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`transition-all duration-300 rounded-full cursor-pointer ${
              i === current
                ? 'w-8 h-2.5 bg-[#EB9830]'
                : 'w-2.5 h-2.5 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}
